import {
  useBulkUpdateSharedKnowledgeFilesApi,
  useBulkUploadSharedKnowledgeFilesApi,
  useCreateSharedKnowledgeGroupApi,
  useDeleteManySharedKnowledgeFilesApi,
  useDeleteSharedKnowledgeGroupApi,
  useDeleteSharedKnowledgeFileApi,
  useDownloadSharedFileApi,
  useSharedKnowledgeGroupsApi,
  useSharedKnowledgeFileByIdApi,
  useSharedKnowledgeFilesApi,
  useSharedKnowledgeFilesForCitationApi,
  useUpdateSharedKnowledgeGroupApi,
  useUpdateSharedKnowledgeFileApi,
  useUploadSharedKnowledgeFileApi,
} from '@/api/sharedFiles';
import { useAuthenticatedClient } from '@/api/useApi';
import { SharedKnowledgeFileFilterParams } from '@ditto/shared';
import { useMemo, useState } from 'react';

export const useUploadSharedKnowledgeFileService = () => {
  const uploadMutation = useUploadSharedKnowledgeFileApi();
  const [isUploading, setIsUploading] = useState(false);
  const { isLoaded, isSignedIn } = useAuthenticatedClient();

  const uploadFile = async (file: File) => {
    if (!isLoaded || !isSignedIn) {
      throw new Error('User not authenticated');
    }

    setIsUploading(true);
    try {
      const response = await uploadMutation.mutateAsync(file);
      return response;
    } finally {
      setIsUploading(false);
    }
  };

  return {
    uploadFile,
    isUploading,
    error: uploadMutation.error,
    isLoaded,
    isSignedIn,
  };
};

export const useSharedKnowledgeFilesService = (
  page: number,
  limit: number,
  filters?: SharedKnowledgeFileFilterParams
) => {
  const { data, ...rest } = useSharedKnowledgeFilesApi(page, limit, filters);

  const files = useMemo(() => {
    return data?.data ?? [];
  }, [data]);

  const pagination = data?.pagination;

  return { files, pagination, ...rest };
};

export const useSharedKnowledgeFileByIdService = (fileId: string) => {
  const { data, ...rest } = useSharedKnowledgeFileByIdApi(fileId);
  return { file: data, ...rest };
};

export const useSharedKnowledgeFilesForCitationService = () => {
  const { data, ...rest } = useSharedKnowledgeFilesForCitationApi();

  const sharedKnowledgeFiles = useMemo(() => {
    return data ?? [];
  }, [data]);

  return { sharedKnowledgeFiles, ...rest };
};

export const useSharedKnowledgeGroupsService = () => {
  const { data, ...rest } = useSharedKnowledgeGroupsApi();

  const groups = useMemo(() => {
    return data ?? [];
  }, [data]);

  return { groups, ...rest };
};

export const useCreateSharedKnowledgeGroupService = () => {
  const { mutate: createGroup, ...rest } = useCreateSharedKnowledgeGroupApi();

  return {
    createGroup,
    ...rest,
  };
};

export const useUpdateSharedKnowledgeGroupService = () => {
  const { mutate: updateGroup, ...rest } = useUpdateSharedKnowledgeGroupApi();

  return { updateGroup, ...rest };
};

export const useDeleteSharedKnowledgeGroupService = () => {
  const { mutate: deleteGroup, ...rest } = useDeleteSharedKnowledgeGroupApi();

  return { deleteGroup, ...rest };
};

export const useUpdateSharedKnowledgeFileService = () => {
  const { mutate: updateFile, ...rest } = useUpdateSharedKnowledgeFileApi();

  return { updateFile, ...rest };
};

export const useDeleteSharedKnowledgeFileService = () => {
  const { mutate: deleteFile, ...rest } = useDeleteSharedKnowledgeFileApi();

  return {
    deleteFile,
    ...rest,
  };
};

export const useDeleteManySharedKnowledgeFilesService = () => {
  const { mutate: deleteManyFiles, ...rest } =
    useDeleteManySharedKnowledgeFilesApi();

  return {
    deleteManyFiles,
    ...rest,
  };
};

export const useDownloadSharedFileService = (fileId: string | undefined) => {
  const { data: sharedKnowledgeFile, ...rest } =
    useDownloadSharedFileApi(fileId);
  return {
    sharedKnowledgeFile,
    ...rest,
  };
};

export const useBulkUploadSharedKnowledgeFilesService = () => {
  const bulkUploadMutation = useBulkUploadSharedKnowledgeFilesApi();
  const [isUploading, setIsUploading] = useState(false);
  const [uploadingFiles, setUploadingFiles] = useState<File[]>([]);
  const { isLoaded, isSignedIn } = useAuthenticatedClient();

  const uploadFiles = async (files: File[]) => {
    if (!isLoaded || !isSignedIn) {
      throw new Error('User not authenticated');
    }

    setIsUploading(true);
    setUploadingFiles(files);
    try {
      const response = await bulkUploadMutation.mutateAsync(files);
      return response;
    } finally {
      setIsUploading(false);
      setUploadingFiles([]);
    }
  };

  return {
    uploadFiles,
    isUploading,
    uploadingFiles,
    error: bulkUploadMutation.error,
    isLoaded,
    isSignedIn,
  };
};

export const useBulkUpdateSharedKnowledgeFilesService = () => {
  const bulkUpdateMutation = useBulkUpdateSharedKnowledgeFilesApi();

  const bulkUpdateFiles = async (fileIds: string[], groupId: string) => {
    const response = await bulkUpdateMutation.mutateAsync({
      fileIds,
      groupId,
    });
    return response;
  };

  return {
    bulkUpdateFiles,
    isPending: bulkUpdateMutation.isPending,
    error: bulkUpdateMutation.error,
  };
};
